import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import {  LucideAngularModule } from 'lucide-angular';
import {
  ChevronDown,
  Search,
  List,
  Zap,
  Edit2,
  Plus,
  RefreshCcw,Trash,ChevronRight, X 
} from 'lucide-angular';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { SurveyListService } from '../../survey-list-service/survey-list';
import { SurveyList,ListType, Element } from '@hyper-inferno/survey-elements-core';
import { SurveyListOptionService } from '../../survey-list-option/survey-list-option';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { SurveyBuilderService } from '../../survey-builder/survey-builder.service';
import { ActivatedRoute } from '@angular/router';
import { ListOptionManager } from "../list-option-manager/list-option-manager";
import { InputComponent } from '../../input/input/input.component';
import { SurveyListStore } from '../../base-classes/survey-list-controller.state';

@Component({
  selector: 'lib-add-item-dialog',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    CommonModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatSelectModule,
    LucideAngularModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatRippleModule,
    MatTooltipModule,
    ListOptionManager,
    InputComponent
],
  changeDetection: ChangeDetectionStrategy.Default, 
  templateUrl: './add-item-dialog.component.html',
  styleUrl: './add-item-dialog.component.scss',
})
export class AddItemDialogComponent {
  readonly store = inject(SurveyListStore);
  readonly dialogRef = inject(MatDialogRef<AddItemDialogComponent>);
  readonly data = inject(MAT_DIALOG_DATA);
  @ViewChild(ListOptionManager) listOptionManager!: ListOptionManager;
  
  readonly ChevronDownIcon = ChevronDown;
  readonly SearchIcon = Search;
  readonly ListIcon = List;
  readonly ZapIcon = Zap;
  readonly EditIcon = Edit2;
  readonly PlusIcon = Plus;
  readonly RefreshIcon = RefreshCcw;
  readonly TrashIcon =Trash
  readonly ChevronRightIcon =ChevronRight;
  readonly XIcon = X;
  instructions = [
    { id: 'AIC', label: 'Add if chosen' },
    { id: 'ANC', label: 'Add if not chosen' },
    { id: 'RIC', label: 'Remove if chosen' },
    { id: 'ADD', label: 'Add items' },
    { id: 'REMOVE', label: 'Remove items' },
    { id: 'RANDOMIZE', label: 'Randomize items' },
    { id: 'LIST_LENGTH', label: 'Set list length' },
    { id: 'CUSTOM_SCRIPT', label: 'Custom (JavaScript)' }
  ];

  activeListName = '';
  activeListId!: string;
  limitTypes = ['atleast', 'atmost', 'exactly'];
  positions = ['top of list', 'custom position'];
  dynamicItems: string[] = ['whole list', 'single item', 'range'];
  selectedDynamicItem: string = '';
  selectedSource = '';
  itemSource = '';
  selectedPos = '';
  specifyPos = false;
  specifyRange = false;
  deletedInstructionIds: string[] = [];
  currentParentListId!: string;
  private initialInstructionSets: any[] = [];
  hasChanges = false;
  private initialSelectedSource = '';
  private normalListHasChanges = false;
  isEditingTitle = false;
  titleControl = new FormControl('');
  @ViewChild('titleInput') titleInput!: ElementRef;
  currentConstructedListId: any;

  instructionSets: any[] = [
  {
     id: null,
    selectedInstr: null,
    itemSource: null,
    questionSource: null,
    selectedDynamicItem: null,
    selectedItems: [],
    specifyPos: false,
    selectedPos: null,
    customPosition: null,
    singleItemValue: null,  
    rangeStart: null,       
    rangeEnd: null  
  }
  ];

  addInstruction() {
    this.instructionSets.push({
      id: null,
      selectedInstr: 'AIC', 
      itemSource: this.selectedSource || null,
      questionSource: null,
      selectedDynamicItem: null,
      selectedItems: [],
      specifyPos: false,
      selectedPos: null,
      customPosition: null,
      singleItemValue: null,  
      rangeStart: null,       
      rangeEnd: null           
    });
    this.detectChanges(); 
  }

  dropdownOpenStates: boolean[] = [];
  toggleOptionDropdownForSet(index: number) {
    this.dropdownOpenStates[index] = !this.dropdownOpenStates[index];
    this.detectChanges();
  }

  // Toggle item selection for specific instruction 
  toggleOptionForSet(setIndex: number, item: string) {
    const currentItems = this.instructionSets[setIndex].selectedItems;
    if (currentItems.includes(item)) {
      this.instructionSets[setIndex].selectedItems = currentItems.filter((i:any) => i !== item);
    } else {
      this.instructionSets[setIndex].selectedItems = [...currentItems, item];
    }
    this.detectChanges(); 
  }

  // Remove option from specific instruction 
  removeOptionFromSelected(setIndex: number, item: string) {
    this.instructionSets[setIndex].selectedItems = 
    this.instructionSets[setIndex].selectedItems.filter((i:any) => i !== item);
    this.detectChanges();
  }

  async removeInstruction(index: number) {
    if (this.instructionSets.length > 1) {
      const removedInstruction = this.instructionSets[index];
      
      if (removedInstruction.id) {
        this.deletedInstructionIds.push(removedInstruction.id);
      }
      
      this.instructionSets.splice(index, 1);
      this.dropdownOpenStates.splice(index, 1);
      delete this.instructionListItems[index];
      
      this.detectChanges();
      this.cdr.detectChanges();
    }
  }
  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.relative')) {
      // Reset all dropdown states
      for (let i = 0; i < this.dropdownOpenStates.length; i++) {
        this.dropdownOpenStates[i] = false;
      }
    }
  }

  listService = inject(SurveyListService);
  private listOptionService = inject(SurveyListOptionService);
  surveyBuilderService = inject(SurveyBuilderService);
  private cdr = inject(ChangeDetectorRef);
  listItems: string[] = [];
  activeType = ListType.NORMAL_LIST;
  ListType= ListType;
  readonly allLists = this.store.lists;
  readonly sourceLists = computed(() => 
    this.allLists().filter(list => list.list_type === ListType.NORMAL_LIST)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
  );
  readonly constructedLists = computed(() => 
    this.allLists().filter(list => list.list_type === ListType.CONSTRUCTED_LIST)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
  );
  selectedSourceList = '';
  questions: Element[] = [];
  // 
  currentListItemsSourceId!: string;
  instructionListItems: { [key: number]: string[] } = {};
  surveyId = signal<number | undefined>(undefined);
  es_entityName = signal<string>('');
  constructor(){
    this.es_entityName.set(this.data?.es_entityName || '');
    const surveyIdFromData = this.data?.surveyId;
    if (surveyIdFromData) {
      this.surveyId.set(surveyIdFromData);
    }
  }
  async ngOnInit() {
    if (!this.surveyId()) {
      console.error('No survey ID available');
      return;
    }
    await this.getList(); 
    const selectedListId = this.data?.selectedListId;
    if (selectedListId) {
      // Find the list by ID and select it
      const list = this.allLists().find(l => l.id === selectedListId);
      if (list) {
        // Select the list based on its type
        await this.selectList(list.name, list.list_type);
      }
    }
    this.hasChanges = false;
    this.dropdownOpenStates = new Array(this.instructionSets.length).fill(false);
    // Load default list items for the first instruction set if it has a source
    if (this.instructionSets[0] && this.instructionSets[0].itemSource) {
      const selectedList = this.allLists().find(l => l.name === this.instructionSets[0].itemSource);
      if (selectedList) {
        this.listItems = await this.getListOptions(selectedList.id);
        this.currentListItemsSourceId = selectedList.id;
      }
    } 
  }
  async selectList(name: string, type: ListType) {
    this.activeListName = name;
    this.titleControl.setValue(name);
    this.activeType = type;
    this.activeListId = '';
    
    if (type === ListType.NORMAL_LIST) {
      const normalList = this.sourceLists().find(l => l.name === name);
      if (normalList) {
        this.activeListId = normalList.id;
        await this.loadQuestions(normalList.id);
        this.saveInitialState();
      }
    } else if (type === ListType.CONSTRUCTED_LIST) {
      const constructedList = this.constructedLists().find(l => l.name === name);
      if (constructedList) {
        this.activeListId = constructedList.id;
        this.currentConstructedListId = constructedList.id;
        this.currentParentListId = constructedList.primary_list_id as string;
        
        // Load questions using the PARENT normal list ID
        if (constructedList.primary_list_id) {
          await this.loadQuestions(constructedList.primary_list_id);
          
          const parentList = this.allLists().find(l => l.id === constructedList.primary_list_id);
          if (parentList) {
            this.selectedSource = parentList.name;
            this.initialSelectedSource = this.selectedSource;
            this.onSelectedSourceChange(this.selectedSource, false);
          }
        }
        
        await this.loadInstructionsForList(constructedList.id);
        this.saveInitialState();
      }
    }
    
    this.cdr.detectChanges();
  }

  detectChanges() {
    const hasInstructionChanges = this.hasInstructionSetsChanged();
    const hasSourceChanged = this.selectedSource !== this.initialSelectedSource;
    const hasDeletedInstructions = this.deletedInstructionIds.length > 0;
    
    // Include normal list changes
    const newHasChanges = hasInstructionChanges || hasSourceChanged || hasDeletedInstructions || this.normalListHasChanges;
    
    this.hasChanges = newHasChanges;
    this.cdr.detectChanges();
  }

  onNormalListChanges(hasChanges: boolean) {
    this.normalListHasChanges = hasChanges;
    this.updateSaveButtonState();
  }

  private updateSaveButtonState() {
    const hasInstructionChanges = this.hasInstructionSetsChanged();
    const hasSourceChanged = this.selectedSource !== this.initialSelectedSource;
    const hasDeletedInstructions = this.deletedInstructionIds.length > 0;
    
    this.hasChanges = hasInstructionChanges || hasSourceChanged || hasDeletedInstructions || this.normalListHasChanges;
    this.cdr.detectChanges();
  }
// Compare current instruction sets with initial state
  private hasInstructionSetsChanged(): boolean {
    if (this.instructionSets.length !== this.initialInstructionSets.length) {
      return true;
    }
    return JSON.stringify(this.instructionSets) !== JSON.stringify(this.initialInstructionSets);
  }
    
  private saveInitialState() {
    this.initialInstructionSets = JSON.parse(JSON.stringify(this.instructionSets));
    this.initialSelectedSource = this.selectedSource;
    this.hasChanges = false;
  }
  async loadInstructionsForList(constructedListId: string) {
    try {
      const instructions = await firstValueFrom(
        this.listService.getInstructionsByListId(constructedListId)
      );
      
      if (instructions && instructions.length > 0) {
        this.instructionSets = [];
        
        for (let idx = 0; idx < instructions.length; idx++) {
          const instr = instructions[idx];
          const instructionSet: any = {
            id: instr.id, 
            selectedInstr: instr.function,
            selectedItems: []
          };
          
          // Get source list name
          instructionSet.itemSource = this.getSourceListNameById(instr.function_list_id);
          
          
          // Load list items for this source
          if (instructionSet.itemSource) {
            const selectedList = this.allLists().find(l => l.name === instructionSet.itemSource);
            if (selectedList) {
              const allOptions = await firstValueFrom(
                this.listOptionService.getListOptions(selectedList.id)
              );
                // Sort by option_key
                const sortedOptions = allOptions.sort((a, b) => 
                Number(a.option_key) - Number(b.option_key)
              );
              this.instructionListItems[idx] = sortedOptions.map((opt: any) => opt.option_display);
            }
          }
          
          switch (instr.function) {
            case 'AIC':
            case 'ANC':
            case 'RIC':
              const savedId = instr.function_question_id;
              // CHECK: Does this ID exist in our currently loaded questions?
              const questionExists = this.questions.some(q => q.id === savedId);
              // Assign the ID if valid, otherwise clear it to null
              instructionSet.questionSource = questionExists ? savedId : null;
              if (instr.function !== 'RIC') {
                  instructionSet.specifyPos = instr.parameter_1 === 'true';
                  instructionSet.selectedPos = instr.parameter_2;
                  instructionSet.customPosition = instr.parameter_3;
                }
              break;

            case 'ADD':
              // Load target position settings (parameters 1-3) - for ALL
              instructionSet.specifyPos = instr.parameter_1 === 'true';
              instructionSet.selectedPos = instr.parameter_2;
              instructionSet.customPosition = instr.parameter_3;
              
              // Check if source is a constructed list
              const isConstructed = this.isConstructedList(instructionSet.itemSource);
              
              if (isConstructed && instr.type) {
                // Load dynamic list settings (parameters 4-6)
                instructionSet.selectedDynamicItem = instr.type;
                
                if (instr.type === 'single item') {
                  instructionSet.singleItemValue = instr.parameter_4;  // Read from parameter_4
                } else if (instr.type === 'range') {
                  instructionSet.rangeStart = instr.parameter_5;  // Read from parameter_5
                  instructionSet.rangeEnd = instr.parameter_6;    // Read from parameter_6
                }
              } else {
                instructionSet.selectedDynamicItem = '';
                // Load selected items for normal list
                const optionTargets = await firstValueFrom(
                  this.listService.getInstructionOptionTargets(instr.id)
                );
                if (optionTargets && optionTargets.length > 0) {
                  const optionIds = optionTargets.map((t: any) => t.option_id);
                  const options = await this.getOptionsByIds(instr.function_list_id, optionIds);
                  instructionSet.selectedItems = options.map((opt: any) => opt.option_display);
                }
              }
              break;

            case 'REMOVE':
              const isConstructedForRemove = this.isConstructedList(instructionSet.itemSource);
              
              if (isConstructedForRemove && instr.type) {
                instructionSet.selectedDynamicItem = instr.type;
                if (instr.type === 'single item') {
                  instructionSet.singleItemValue = instr.parameter_4 || instr.parameter_3;
                } else if (instr.type === 'range') {
                  const rangeValue = instr.parameter_4 || instr.parameter_3;
                  const [start, end] = (rangeValue || '').split(',');
                  instructionSet.rangeStart = start;
                  instructionSet.rangeEnd = end;
                }
              } else {
                instructionSet.selectedDynamicItem = '';
                const optionTargets = await firstValueFrom(
                  this.listService.getInstructionOptionTargets(instr.id)
                );
                if (optionTargets && optionTargets.length > 0) {
                  const optionIds = optionTargets.map((t: any) => t.option_id);
                  const options = await this.getOptionsByIds(instr.function_list_id, optionIds);
                  instructionSet.selectedItems = options.map((opt: any) => opt.option_display);
                }
              }
              break;
              
            case 'RANDOMIZE':
              instructionSet.specifyRange = instr.parameter_1 === 'true';
              instructionSet.rangeStart = instr.parameter_2;
              instructionSet.rangeEnd = instr.parameter_3;
              break;
              
            case 'LIST_LENGTH':
              instructionSet.limitType = instr.type;
              instructionSet.limitValue = instr.parameter_1;
              break;
              
            case 'CUSTOM_SCRIPT':
              instructionSet.customScript = instr.parameter_1;
              break;
          }
          
          this.instructionSets.push(instructionSet);
        }
        
        // Set current listItems to the first instruction's items for display
        if (this.instructionSets[0] && this.instructionSets[0].itemSource) {
          const selectedList = this.allLists().find(l => l.name === this.instructionSets[0].itemSource);
          if (selectedList) {
            this.listItems = this.instructionListItems[0] || [];
            this.currentListItemsSourceId = selectedList.id;
          }
        }
        
        // Refresh dropdown states
        this.dropdownOpenStates = new Array(this.instructionSets.length).fill(false);
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Error loading instructions:', error);
    }
  }

  async onSelectedSourceChange(sourceName: string, shouldReset: boolean = true) {
    this.selectedSource = sourceName;
  
    // 1. Find the new Parent List object
    const newParentList = this.sourceLists().find(l => l.name === sourceName);
    
    if (newParentList) {
      // 2. Update lineage for the new "Family"
      // This fetches all questions associated with the NEW root list
      await this.loadQuestions(newParentList.id);
      
      // 3. Update the items available for this specific source
      const validLists = this.getAvailableListsForSource(sourceName);
  
      // 4. Reset instructions if user manually changed the source
      if (shouldReset) {
        this.instructionSets.forEach(set => {
          // If an instruction was pointing to a list not in the new family, clear it
          if (set.itemSource && !validLists.includes(set.itemSource)) {
            set.itemSource = '';
            set.selectedItems = [];
          }
          // IMPORTANT: Clear the question source because the old questions 
          // are no longer compatible with the new list lineage
          set.questionSource = null;
        });
        
        this.instructionListItems = {};
        this.listItems = [];
      }
    }
  
    this.detectChanges();
    this.cdr.detectChanges();
  }
// 
  async createConstructedListFromParent(primary_list_id: string) {
    try {
      this.currentParentListId = primary_list_id;  
       // Reset deleted instructions tracking
      this.deletedInstructionIds = [];
      // Find the parent list name
      const parentList = this.sourceLists().find(l => l.id === primary_list_id);
      if (!parentList) {
        // alert('Parent list not found');
        return;
      }
      
      // 1. Create the constructed list
      const constructedList = await firstValueFrom(
        this.listService.createList({
          list_type: ListType.CONSTRUCTED_LIST,
          survey_id: this.surveyId() as number,
          primary_list_id: primary_list_id
        })
      );
      
      // 2. Refresh lists
      await this.getList();
      
      // 3. Switch to the constructed list UI (but don't save instructions yet)
      this.activeListName = constructedList.name;
      this.activeType = ListType.CONSTRUCTED_LIST;
      this.currentConstructedListId = constructedList.id;
      
      // 4. Reset instruction sets for the new list
      this.instructionSets = [{
        selectedInstr: '',
        itemSource: '',
        selectedDynamicItem: '',
        selectedItems: [],
        specifyPos: false,
        selectedPos: '',
        customPosition: null
      }];
      
      this.dropdownOpenStates = [false];
      
      // 5. Set default source
      this.selectedSource = parentList.name;
      this.initialSelectedSource = this.selectedSource;
      this.saveInitialState();
      // 6. Trigger change detection
      this.cdr.detectChanges();
      
    } catch (error) {
      console.error('Error creating constructed list:', error);
    }
  }

  async saveConstructedListInstructions(constructedListId: string) {
    try {
      // 1. Calculate logic validity before saving
    // Checks if AIC, ANC, or RIC types are missing their questionSource
    const isLogicValid = this.instructionSets.every(instr => {
      if (['AIC', 'ANC', 'RIC'].includes(instr.selectedInstr)) {
        // Return true only if questionSource has a value (not null/undefined/empty)
        return !!instr.questionSource; 
      }
      return true; // Other instruction types (ADD, REMOVE, etc.) are considered valid here
    });
      // Delete removed instructions
      if (this.deletedInstructionIds.length > 0) {
        await firstValueFrom(
          this.listService.deleteInstructions({ instructionIds: this.deletedInstructionIds })
        );
        this.deletedInstructionIds = [];
      }
      
      // Save all instructions
      for (const instrSet of this.instructionSets) {
        if (instrSet.selectedInstr) {
          if (instrSet.id) {
            // Update existing instruction
            await this.updateInstruction(
              instrSet.id, 
              this.currentParentListId,  // ← instruction_set_list_id (parent normal list)
              constructedListId,          // ← list_id (owner constructed list)
              instrSet
            );
          } else {
            // Create new instruction
            await this.saveInstruction(
              this.currentParentListId,   // ← instruction_set_list_id (parent normal list)
              constructedListId,          // ← list_id (owner constructed list)
              instrSet
            );
          }
        }
      }
      this.normalListHasChanges = false;
      this.saveInitialState();
    // Reset ListOptionManager change tracking if it exists and we're on a normal list
    if (this.listOptionManager && this.activeType === ListType.NORMAL_LIST) {
      this.listOptionManager.resetChangeTracking();
    }
    if (this.activeType === ListType.NORMAL_LIST && this.activeListId) {
      this.listOptionService.notifyListUpdated(this.activeListId);
    }
      this.updateSaveButtonState();
      this.dialogRef.close({ 
          success: true,
          listId: constructedListId,
          isLogicValid: isLogicValid
         });
    } catch (error) {
      console.error('Error saving instructions:', error);
    }
  }
  onDone(): void {
    if (this.activeType === ListType.CONSTRUCTED_LIST && this.currentConstructedListId) {
      // Save instructions when user clicks Save Changes
      this.saveConstructedListInstructions(this.currentConstructedListId);
    } else if (this.activeType === ListType.NORMAL_LIST) {
      this.resetListOptionManager();
      setTimeout(() => {
        if (this.activeListId) {
          this.listOptionService.notifyListUpdated(this.activeListId);
        }
        this.dialogRef.close({ success: true });
      }, 100);
    } else {
      this.dialogRef.close();
    }
  }
  showCancelWarning = false;

  onCancel(): void {
    if (!this.hasChanges) {
      this.closeDialog();
      return;
    }
    this.showCancelWarning = true;
    this.cdr.detectChanges();
  }
  
  stayAndEdit(): void {
    this.showCancelWarning = false;
    this.cdr.detectChanges();
  }
  
  closeDialog(): void {
    this.resetListOptionManager();
    this.dialogRef.close({ success: false });
  }
  async saveConstructedList(
    constructedListId: string,  // owner constructed list ID (list_id)
    parentListId: string         // parent normal list ID (instruction_set_list_id)
  ) {
    for (const instrSet of this.instructionSets) {
      if (instrSet.selectedInstr) {
        if (instrSet.id) {
          // Update existing instruction
          await this.updateInstruction(
            instrSet.id, 
            parentListId,        // instruction_set_list_id
            constructedListId,   // list_id
            instrSet
          );
        } else {
          // Create new instruction
          await this.saveInstruction(
            parentListId,        // instruction_set_list_id
            constructedListId,   // list_id
            instrSet
          );
        }
      }
    }
  }

  private async saveInstruction(
    instructionSetListId: string, // parent normal list ID
    listId: string,  // owner constructed list ID
    instrSet: any
  ) {
    switch(instrSet.selectedInstr) {
      case 'AIC':
      await this.saveAICInstruction(instructionSetListId, listId, instrSet);
      break;
      case 'ANC':
      await this.saveANCInstruction(instructionSetListId, listId, instrSet);
      break;
      case 'RIC':
      await this.saveRICInstruction(instructionSetListId, listId, instrSet);
      break;
      case 'ADD':
        await this.saveAddInstruction(instructionSetListId, listId, instrSet);
        break;
      case 'REMOVE':
        await this.saveRemoveInstruction(instructionSetListId, listId, instrSet);
        break;
      case 'RANDOMIZE':
        await this.saveRandomizeInstruction(instructionSetListId, listId, instrSet);
        break;
      case 'LIST_LENGTH':
        await this.saveListLengthInstruction(instructionSetListId, listId, instrSet);
        break;
      case 'CUSTOM_SCRIPT':
        await this.saveCustomScriptInstruction(instructionSetListId, listId, instrSet);
        break;
    }
  }

  private async saveAICInstruction(
    instructionSetListId: string,
    listId:string,
    instrSet: any
  ) {
    let parameter_1 = "false";
    let parameter_2 = null;
    let parameter_3 = null;
    
    // Find question by id
    const questionId = Number(instrSet.questionSource);
    const question = this.questions.find(q => q.id === questionId);
    
    if (instrSet.specifyPos) {
      parameter_1 = "true";
      parameter_2 = instrSet.selectedPos;
      
      if (instrSet.selectedPos === 'custom position') {
        parameter_3 = instrSet.customPosition;
      }
    }
  
     await firstValueFrom(
      this.listService.createInstruction({
        instruction_set_list_id: instructionSetListId,
        list_id:listId,
        function: 'AIC',
        function_list_id: undefined,
        function_question_id: question?.id,
        type: undefined,
        parameter_1: parameter_1,
        parameter_2: parameter_2,
        parameter_3: parameter_3,
        parameter_4: undefined,
        parameter_5: undefined,
        parameter_6: undefined
      })
    );
  }
  
  private async saveANCInstruction(
    instructionSetListId: string,
    listId:string,
    instrSet: any
  ) {
    let parameter_1 = "false";
    let parameter_2 = null;
    let parameter_3 = null;
    
    // Find question by id
    const questionId = Number(instrSet.questionSource);
    const question = this.questions.find(q => q.id === questionId);
    
    if (instrSet.specifyPos) {
      parameter_1 = "true";
      parameter_2 = instrSet.selectedPos;
      
      if (instrSet.selectedPos === 'custom position') {
        parameter_3 = instrSet.customPosition;
      }
    }
  
    await firstValueFrom(
      this.listService.createInstruction({
        instruction_set_list_id: instructionSetListId,
        list_id:listId,
        function: 'ANC',
        function_list_id: undefined,
        function_question_id: question?.id,
        type: undefined,
        parameter_1: parameter_1,
        parameter_2: parameter_2,
        parameter_3: parameter_3,
        parameter_4: undefined,
        parameter_5: undefined,
        parameter_6: undefined
      })
    );
  }
  
  private async saveRICInstruction(
    instructionSetListId: string,
    list_id:string,
    instrSet: any
  ) {
    // Find question by id
    const questionId = Number(instrSet.questionSource);
    const question = this.questions.find(q => q.id === questionId);
    
     await firstValueFrom(
      this.listService.createInstruction({
        instruction_set_list_id: instructionSetListId,
        list_id:list_id,
        function: 'RIC',
        function_list_id: undefined,
        function_question_id: question?.id,
        type: undefined,
        parameter_1: undefined,
        parameter_2: undefined,
        parameter_3: undefined,
        parameter_4: undefined,
        parameter_5: undefined,
        parameter_6: undefined
      })
    );
  }
  
  private async saveAddInstruction(
    instructionSetListId: string,  // parent normal list ID
    listId: string,// owner constructed list ID
    instrSet: any
  ) {
    // Find the source list (where items are coming FROM)
    const sourceList = this.allLists().find(
      l => l.name === instrSet.itemSource
    );
  
    // Check if source is a constructed list (dynamic)
    const isDynamic = this.isConstructedList(instrSet.itemSource);
  
    // Parameters 1-3: Target position (WHERE to put) - for ALL instructions
    let parameter_1 = "false";  // Target position flag
    let parameter_2 = null;      // Target position type
    let parameter_3 = null;      // Target custom position value
    
    // Parameters 4-6: Source dynamic list values (WHAT to take) - ONLY for dynamic lists
    let parameter_4 = null;      // Single item position
    let parameter_5 = undefined;      // Range start
    let parameter_6 = undefined;      // Range end
    
    // Handle target position (parameters 1-3) - applies to ALL
    if (instrSet.specifyPos) {
      parameter_1 = "true";
      parameter_2 = instrSet.selectedPos;  // "top of list" or "custom position"
      
      if (instrSet.selectedPos === 'custom position') {
        parameter_3 = instrSet.customPosition;  // Custom position value
      }
    }
  
    // Handle dynamic list source values (parameters 4-6) - ONLY for dynamic lists
    if (isDynamic) {
      if (instrSet.selectedDynamicItem === 'single item') {
        parameter_4 = instrSet.singleItemValue;  // Single item position in parameter_4
      } else if (instrSet.selectedDynamicItem === 'range') {
        parameter_5 = instrSet.rangeStart;  // Range start in parameter_5
        parameter_6 = instrSet.rangeEnd;    // Range end in parameter_6
      }
      // For 'whole list', all parameters 4-6 stay null
    }
  
    // Determine type for database
    let type = null;
    if (isDynamic) {
      type = instrSet.selectedDynamicItem;  // 'single item', 'range', or 'whole list'
    }
  
    // Create the instruction record
    const instruction = await firstValueFrom(
      this.listService.createInstruction({
        instruction_set_list_id: instructionSetListId,
        list_id: listId,
        function: 'ADD',
        function_list_id: sourceList?.id,
        type: type,
        parameter_1: parameter_1,  // Target position flag
        parameter_2: parameter_2,  // Target position type
        parameter_3: parameter_3,  // Target custom position value
        parameter_4: parameter_4,  // Single item position (for dynamic single item)
        parameter_5: parameter_5,  // Range start (for dynamic range)
        parameter_6: parameter_6   // Range end (for dynamic range)
      })
    );
  
    // If specific items were selected from a normal list, save them to the join table
    if (!isDynamic && instrSet.selectedItems && instrSet.selectedItems.length > 0) {
      const options = await this.getOptionsByDisplayNames(
        sourceList!.id, 
        instrSet.selectedItems
      );
      for (const option of options) {
        await firstValueFrom(
          this.listService.addInstructionOptionTarget(
            instruction.id, 
            option.id
          )
        );
      }
    }
  }

  private async saveRemoveInstruction(
    instructionSetListId: string,
    listId:string,
    instrSet: any
  ) {
    // Find the source list (where items are coming FROM)
    const sourceList = this.allLists().find(
      l => l.name === instrSet.itemSource
    );

    let parameter_3 = null;
    
    // Determine the type based on what was selected
    let type = null;
    
    // Check if this is a dynamic list (name contains 'Dynamic')
    if (instrSet.itemSource && instrSet.itemSource.includes('Dynamic')) {
      // For dynamic lists, use the selectedDynamicItem ('whole list', 'single item', 'range')
      type = instrSet.selectedDynamicItem;
      
      // Handle parameter_3 for dynamic list ranges/single items
      if (instrSet.selectedDynamicItem === 'single item') {
        parameter_3 = instrSet.singleItemValue;
      } else if (instrSet.selectedDynamicItem === 'range') {
        parameter_3 = `${instrSet.rangeStart},${instrSet.rangeEnd}`;
      }
    } else {
      // For normal lists, type should be NULL when specific items are selected
      type = null;
    }

    // Create the instruction record
    const instruction = await firstValueFrom(
      this.listService.createInstruction({
        instruction_set_list_id: instructionSetListId,
        list_id: listId,
        function: 'REMOVE',
        function_list_id: sourceList?.id,
        type: type,  // NULL for normal list with specific items
        parameter_1: undefined,
        parameter_2: undefined,
        parameter_3: parameter_3
      })
    );

    // If specific items were selected from a normal list, save them to the join table
    if (instrSet.selectedItems && instrSet.selectedItems.length > 0) {
      const options = await this.getOptionsByDisplayNames(
        sourceList!.id, 
        instrSet.selectedItems
      );
      
      for (const option of options) {
        await firstValueFrom(
          this.listService.addInstructionOptionTarget(
            instruction.id, 
            option.id
          )
        );
      }
    }
  }

  private async saveRandomizeInstruction(
    instructionSetListId: string,
    listId:string,
    instrSet: any
  ) {
    let parameter_1 = "false"; // Default: no range specified
    let parameter_2 = null;    // Start position
    let parameter_3 = null;    // End position
    
    if (instrSet.specifyRange) {
      parameter_1 = "true";
      parameter_2 = instrSet.rangeStart;  // Start position
      parameter_3 = instrSet.rangeEnd;    // End position
    }
  
     await firstValueFrom(
      this.listService.createInstruction({
        instruction_set_list_id: instructionSetListId,
        list_id:listId,
        function: 'RANDOMIZE',
        function_list_id: undefined,
        type: undefined,
        parameter_1: parameter_1,  // "true" or "false"
        parameter_2: parameter_2,  // Start position
        parameter_3: parameter_3   // End position
      })
    );
  }

  private async saveListLengthInstruction(
    instructionSetListId: string,
    listId:string,
    instrSet: any
  ) {
     await firstValueFrom(
      this.listService.createInstruction({
        instruction_set_list_id: instructionSetListId,
        list_id:listId,
        function: 'LIST_LENGTH',
        function_list_id: undefined,
        type: instrSet.limitType,      // 'atleast', 'atmost', or 'exactly'
        parameter_1: instrSet.limitValue, // The numeric value
        parameter_2: undefined,
        parameter_3: undefined
      })
    );
  }

  private async saveCustomScriptInstruction(
    instructionSetListId: string,
    listId:string,
    instrSet: any
  ) {
     await firstValueFrom(
      this.listService.createInstruction({
        instruction_set_list_id: instructionSetListId,
        list_id:listId,
        function: 'CUSTOM_SCRIPT',
        function_list_id: undefined,
        function_question_id:undefined,
        type: undefined,
        parameter_1: instrSet.customScript, // The JavaScript code
        parameter_2: undefined,
        parameter_3: undefined
      })
    );
  }
  async loadQuestions(list_id:string) {
    try {
      const questions = await lastValueFrom(
        this.surveyBuilderService.getElementsByListLineage(list_id),
      );
      this.questions = questions;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  }
  async getList(){
    await this.store.loadLists(this.surveyId() as number);
  }

  async getListOptions(list_id: string): Promise<string[]> {
    const options = await firstValueFrom(
      this.listOptionService.getListOptions(list_id),
    );
        // Sort options by option_key
        const sortedOptions = options.sort((a, b) => 
        Number(a.option_key) - Number(b.option_key)
        );
    return sortedOptions
      .map(option => option.option_display)
      .filter((display): display is string => display !== null);
  }
  private async getOptionsByDisplayNames(
    listId: string, 
    displayNames: string[]
  ): Promise<any[]> {
    const options = await firstValueFrom(
      this.listOptionService.getListOptions(listId)
    );
    return options.filter((opt:any) => 
      displayNames.includes(opt.option_display)
    );
  }
  async getOptionsByIds(listId: string, optionIds: string[]): Promise<any[]> {
    const allOptions = await firstValueFrom(
      this.listOptionService.getListOptions(listId)
    );
    // Filter by id, not option_display
    return allOptions.filter((opt: any) => optionIds.includes(opt.id));
  }
  getSourceListNameById(listId: string | null): string {
    if (!listId) return '';
    const list = this.allLists().find(l => l.id === listId);
    return list ? list.name : '';
  }

  async onItemSourceChange(instrSet: any, index: number) {
    if (instrSet.itemSource) {
      const selectedList = this.allLists().find(l => l.name === instrSet.itemSource);
      if (selectedList) {
        const items = await this.getListOptions(selectedList.id);
        this.instructionListItems[index] = items;
        this.listItems = items; // For current view
        this.currentListItemsSourceId = selectedList.id;
        
        instrSet.selectedItems = [];
        instrSet.selectedDynamicItem = '';
      }
    } else {
      this.instructionListItems[index] = [];
      this.listItems = [];
      instrSet.selectedItems = [];
    }
    this.detectChanges(); 
    this.cdr.detectChanges();
  }
  isConstructedList(listName: string): boolean {
    if (!listName) return false;
    return this.constructedLists().some(list => list.name === listName);
  }
  getAvailableListsForSource(selectedSource: string): string[] {
    if (!selectedSource) return [];
  
    // 1. Find the parent source list object
    const sourceListObj = this.sourceLists().find(l => l.name === selectedSource);
    if (!sourceListObj) return [];
  
    // 2. Filter lists:
    // - Must be the parent OR have the parent as its primary_list_id
    // - MUST NOT be the list we are currently editing (prevent self-reference)
    const filtered = this.store.lists().filter(list => {
      const isParentOrSibling = (list.name === selectedSource || list.primary_list_id === sourceListObj.id);
      const isNotSelf = list.name !== this.activeListName; // Exclude current list
      
      return isParentOrSibling && isNotSelf;
    });
  
    return filtered.map(l => l.name);
  }
  
  private async updateInstruction(
    instructionId: string,
    instructionSetListId: string,
    listId: string, 
    instrSet: any
  ) {
    // First delete old option targets
    await firstValueFrom(
      this.listService.deleteInstructionOptionTargets(instructionId)
    );
    
    let function_list_id = null;
    let function_question_id = null;
    let type = null;
    let parameter_1 = null;
    let parameter_2 = null;
    let parameter_3 = null;
    let parameter_4 = null;
    let parameter_5 = null;
    let parameter_6 = null;
    
    if (instrSet.selectedInstr === 'ADD' || instrSet.selectedInstr === 'REMOVE') {
      const sourceList = this.allLists().find(l => l.name === instrSet.itemSource);
      function_list_id = sourceList?.id;
      const isDynamic = this.isConstructedList(instrSet.itemSource);
      
      if (isDynamic) {
        type = instrSet.selectedDynamicItem;
        if (instrSet.selectedDynamicItem === 'single item') {
          parameter_4 = instrSet.singleItemValue;
        } else if (instrSet.selectedDynamicItem === 'range') {
          parameter_5 = instrSet.rangeStart;
          parameter_6 = instrSet.rangeEnd;
        }
      }
      
      if (instrSet.specifyPos) {
        parameter_1 = "true";
        parameter_2 = instrSet.selectedPos;
        if (instrSet.selectedPos === 'custom position') {
          parameter_3 = instrSet.customPosition;
        }
      }
      
      if (!isDynamic && instrSet.selectedItems && instrSet.selectedItems.length > 0) {
        const options = await this.getOptionsByDisplayNames(sourceList!.id, instrSet.selectedItems);
        for (const option of options) {
          await firstValueFrom(
            this.listService.addInstructionOptionTarget(instructionId, option.id)
          );
        }
      }
    } 
    else if (instrSet.selectedInstr === 'AIC' || instrSet.selectedInstr === 'ANC') {
      const questionId = Number(instrSet.questionSource);
      function_question_id = questionId;
      
      if (instrSet.specifyPos) {
        parameter_1 = "true";
        parameter_2 = instrSet.selectedPos;
        if (instrSet.selectedPos === 'custom position') {
          parameter_3 = instrSet.customPosition;
        }
      }
    }
    else if (instrSet.selectedInstr === 'RIC') {
      const questionId = Number(instrSet.questionSource);
      function_question_id = questionId;
    }
    else if (instrSet.selectedInstr === 'RANDOMIZE') {
      if (instrSet.specifyRange) {
        parameter_1 = "true";
        parameter_2 = instrSet.rangeStart;
        parameter_3 = instrSet.rangeEnd;
      }
    }
    else if (instrSet.selectedInstr === 'LIST_LENGTH') {
      type = instrSet.limitType;
      parameter_1 = instrSet.limitValue;
    }
    else if (instrSet.selectedInstr === 'CUSTOM_SCRIPT') {
      parameter_1 = instrSet.customScript;
    }
  
    // Update instruction
    await firstValueFrom(
      this.listService.updateInstruction({
        id: instructionId,
        instruction_set_list_id: instructionSetListId,
        list_id: listId, 
        function: instrSet.selectedInstr,
        function_list_id: function_list_id,
        function_question_id: function_question_id,
        type: type,
        parameter_1: parameter_1,
        parameter_2: parameter_2,
        parameter_3: parameter_3,
        parameter_4: parameter_4,
        parameter_5: parameter_5,
        parameter_6: parameter_6
      })
    );
  }
  getConstructedListsByParent(parentId: string): SurveyList[] {
    return this.constructedLists().filter(list => list.primary_list_id === parentId);
  }
  private resetListOptionManager() {
    if (this.listOptionManager) {
      // Clear the active item
      this.listOptionManager.activeItemId.set(null);
      // Reset the form
      this.listOptionManager.form.reset();
      // Reload the items for the current list
      const currentListId = this.activeListId;
      if (currentListId) {
        this.listOptionManager.reloadItems(currentListId);
      }
    }
  }

  async saveTitle() {
    const newName: string = this.titleControl.value ?? '';
  
    if (!this.activeListId || this.activeListId === 'undefined' || !newName.trim()) {
      this.isEditingTitle = false;
      this.hasChanges = false;
      return;
    }
  
    // 1. Update Normal Lists (Parents)
    this.store.updateListNameLocally(this.activeListId,newName)
    this.hasChanges = true;
  
    // 3. API Call
    this.listService.updateList({
      name: newName,
      list_id: this.activeListId
    }).subscribe({
      next: () => {
        this.activeListName = newName;
        this.isEditingTitle = false;
      },
      error: (err) => {
        this.titleControl.setValue(this.activeListName);
        this.isEditingTitle = false;
        this.hasChanges = false;
      }
    });
  }
// 
  createNormalList() {
    const currentActiveId = this.store.activeListId();
    const surveyId = this.surveyId();
    if (!surveyId) return;

    this.store.createListAndUpdateEntity({
      list_type: ListType.NORMAL_LIST,
      survey_id: surveyId,
      es_entityName: this.es_entityName(), 
    });
    this.store.setActiveList(currentActiveId);
  }
}
